import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthReady } from "@/hooks/useAuthReady";
import { PLAN_LIMITS, PLANS, USAGE_CAPS, PlanId, FeatureKey } from "@/config/plans";

// Re-export for backward compatibility
export { PLAN_LIMITS, PLANS, type PlanId, type FeatureKey } from "@/config/plans";

interface SubscriptionData {
  plan: PlanId;
  subscribed: boolean;
  subscriptionEnd: string | null;
  billingInterval: "monthly" | "annual";
  isTeamPlan: boolean;
  teamOwnerId: string | null;
  creditsExhausted: boolean;
  ownerUsage: { domain_searches_used: number; email_finds_used: number; verifications_used: number } | null;
}

interface UsageData {
  leadsThisMonth: number;
  templatesCount: number;
}

const SUB_KEY = "subscription";
const USAGE_KEY = "subscription-usage";

const DEFAULT_SUB: SubscriptionData = {
  plan: "starter",
  subscribed: false,
  subscriptionEnd: null,
  billingInterval: "monthly",
  isTeamPlan: false,
  teamOwnerId: null,
  creditsExhausted: false,
  ownerUsage: null,
};

export const useSubscription = () => {
  const { user, isReady } = useAuthReady();
  const queryClient = useQueryClient();

  const {
    data: subscription = DEFAULT_SUB,
    isLoading,
    isSuccess,
  } = useQuery<SubscriptionData>({
    queryKey: [SUB_KEY, user?.id],
    enabled: isReady && !!user,
    staleTime: 60 * 1000, // 1 min
    refetchInterval: 60 * 1000, // background refresh every 60s
    queryFn: async () => {
      const isDemoUser = user!.app_metadata?.is_demo === true || user!.email === 'demo@arrowlink.ai';
      if (isDemoUser) {
        return { ...DEFAULT_SUB, plan: "enterprise" as PlanId, subscribed: true };
      }

      const subPromise = supabase.functions.invoke("check-subscription");
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Subscription check timed out")), 10000),
      );

      const { data, error } = await Promise.race([subPromise, timeoutPromise]);
      if (error) throw error;

      const plan = (data.plan || "starter") as PlanId;
      const caps = USAGE_CAPS[plan] || USAGE_CAPS.starter;
      const usageData = data.usage;
      const exhausted =
        data.credits_exhausted ||
        (usageData &&
          caps.domainSearches > 0 &&
          usageData.domain_searches_used >= caps.domainSearches &&
          caps.verifications > 0 &&
          usageData.verifications_used >= caps.verifications);

      return {
        plan,
        subscribed: data.subscribed,
        subscriptionEnd: data.subscription_end,
        billingInterval: data.billing_interval === "year" ? "annual" : "monthly",
        isTeamPlan: !!data.team_plan,
        teamOwnerId: data.team_owner_id || null,
        creditsExhausted: !!exhausted,
        ownerUsage: usageData || null,
      } as SubscriptionData;
    },
  });

  const { data: usage = { leadsThisMonth: 0, templatesCount: 0 } } = useQuery<UsageData>({
    queryKey: [USAGE_KEY, user?.id],
    enabled: isReady && !!user,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async () => {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const [{ count: leadsCount }, { count: templatesCount }] = await Promise.all([
        supabase
          .from("leads")
          .select("*", { count: "exact", head: true })
          .gte("created_at", startOfMonth.toISOString()),
        supabase.from("email_templates").select("*", { count: "exact", head: true }),
      ]);

      return {
        leadsThisMonth: leadsCount || 0,
        templatesCount: templatesCount || 0,
      };
    },
  });

  const refreshSubscription = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SUB_KEY, user?.id] });
  }, [queryClient, user?.id]);

  const refreshUsage = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [USAGE_KEY, user?.id] });
  }, [queryClient, user?.id]);

  const canUseFeature = useCallback(
    (feature: FeatureKey): boolean => {
      const limits = PLAN_LIMITS[subscription.plan];
      if (typeof limits[feature] === "boolean") return limits[feature] as boolean;
      if (feature === "leads") return usage.leadsThisMonth < (limits.leads as number);
      if (feature === "templates") return usage.templatesCount < (limits.templates as number);
      return true;
    },
    [subscription.plan, usage],
  );

  const getUsagePercentage = useCallback(
    (feature: "leads" | "templates"): number => {
      const limit = PLAN_LIMITS[subscription.plan][feature] as number;
      if (limit === Infinity) return 0;
      const current = feature === "leads" ? usage.leadsThisMonth : usage.templatesCount;
      return Math.min((current / limit) * 100, 100);
    },
    [subscription.plan, usage],
  );

  const getRemainingUsage = useCallback(
    (feature: "leads" | "templates"): number => {
      const limit = PLAN_LIMITS[subscription.plan][feature] as number;
      if (limit === Infinity) return Infinity;
      const current = feature === "leads" ? usage.leadsThisMonth : usage.templatesCount;
      return Math.max(limit - current, 0);
    },
    [subscription.plan, usage],
  );

  return {
    ...subscription,
    isLoading: isLoading && isReady && !!user,
    hasSuccessfullyLoaded: isSuccess,
    usage,
    canUseFeature,
    getUsagePercentage,
    getRemainingUsage,
    refreshSubscription,
    refreshUsage,
    limits: PLAN_LIMITS[subscription.plan],
  };
};
